import {Component, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AdminService} from '../../../services/admin.service';
import {LabelSettings, ProjectSettings, TechnicalLibrarySettings} from '../../../models/admin.model';
import {DialogService} from '../../../services/dialog.service';
import {BehaviorSubject, of} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {UserService} from '../../../services/user.service';
import {catchError, finalize, switchMap, take} from "rxjs/operators";
import {Scope} from "../../../constants/scopes";
import {AuthService} from "../../../services/auth.service";
import {DRIVE_LABELS_OAUTH_TOKEN, PENDING_DRIVE_LABEL_CREATION} from "../../../constants/local-storage-constants";

@Component({
  selector: 'app-settings-management',
  templateUrl: './settings-management.component.html',
  styleUrls: ['./settings-management.component.scss']
})
export class SettingsManagementComponent implements OnInit {

  public projectForm: FormGroup;

  public isLabelCreated = false;
  public isProjectSettingsCreated = false;

  public enableLabelBtn = false;
  public enableProjectSettingsBtn = false;

  public labelSettings: LabelSettings;
  public projectSettings: ProjectSettings;

  public technicalLibraries: TechnicalLibrarySettings;
  public technicalLibraryFields: (keyof TechnicalLibrarySettings)[] =
    ['techLibraryId', 'techLibraryIdClone', 'auditLogLibraryId'];
  public technicalLibrariesLoadingSubject = new BehaviorSubject<boolean>(false);
  public technicalLibrariesLoading$ = this.technicalLibrariesLoadingSubject.asObservable();

  public loadingSubject = new BehaviorSubject<boolean>(false);
  public labelLoadingSubject = new BehaviorSubject<boolean>(false);
  public projectLoadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();
  public labelLoading$ = this.labelLoadingSubject.asObservable();
  public projectLoading$ = this.projectLoadingSubject.asObservable();

  scopes = [Scope.EMAIL, Scope.OPENID, Scope.DRIVE_ADMIN_LABELS].join(' ');

  constructor(private adminService: AdminService,
              private authService: AuthService,
              private dialogService: DialogService,
              private userService: UserService,
              private fb: FormBuilder,
              private translate: TranslateService) {
  }

  ngOnInit(): void {
    this.projectForm = this.fb.group({
      gcpProject: ['', Validators.required],
      bucketName: ['', Validators.required]
    });
    this.getDomainConfiguration();
  }

  async getDomainConfiguration(): Promise<void> {
    this.loadingSubject.next(true);
    await this.fetchSettings();
    this.loadingSubject.next(false);
    if (this.isSuperAdmin()) {
      if (!this.isLabelCreated) {
        this.enableLabelBtn = true;
      }
      if (!this.isProjectSettingsCreated) {
        this.enableProjectSettingsBtn = true;
      }
      this.resumePendingLabelCreation();
    }
  }

  // The labels scope is granted via a full-page redirect, so the click that started
  // the flow cannot complete it: pick the request back up after we land here again.
  private resumePendingLabelCreation(): void {
    if (localStorage.getItem(PENDING_DRIVE_LABEL_CREATION) !== 'true') {
      return;
    }
    localStorage.removeItem(PENDING_DRIVE_LABEL_CREATION);

    const labelsToken = this.authService.getDriveLabelsToken();
    if (!labelsToken) {
      this.dialogService.error('Failed to grant Google Drive labels access. Please try again.');
      return;
    }
    this.createDriveLabel(labelsToken);
  }

  async fetchSettings() {
    this.labelSettings = await this.adminService.getLabelSettings().pipe(
      take(1),
      catchError(error => {
        console.error('Error fetching label settings:', error);
        this.dialogService.complianceError(error.error.message);

        return of(null);
      })
    ).toPromise();

    this.projectSettings = await this.adminService.getProjectSettings().pipe(
      take(1),
      catchError(error => {
        console.error('Error fetching project settings:', error);
        this.dialogService.complianceError(error.error.message);
        return of(null);
      })
    ).toPromise();

    this.technicalLibraries = await this.adminService.getTechnicalLibraries().pipe(
      take(1),
      catchError(error => {
        console.error('Error fetching technical libraries:', error);
        this.dialogService.complianceError(error.error.message);
        return of(null);
      })
    ).toPromise();

    if (!!this.labelSettings) {
      this.isLabelCreated = true;
    }
    if (!!this.projectSettings) {
      this.isProjectSettingsCreated = true;
    }
  }

  createLabelClick(): void {
    localStorage.setItem(PENDING_DRIVE_LABEL_CREATION, 'true');
    this.authService.requestGoogleScope(this.scopes, DRIVE_LABELS_OAUTH_TOKEN);
  }

  createDriveLabel(labelsAccessToken: string): void {
    this.labelLoadingSubject.next(true);
    this.adminService.createDriveLabel(labelsAccessToken).pipe(
      finalize(() => this.labelLoadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        this.labelSettings = resp;
        this.enableLabelBtn = false;
        this.dialogService.info(this.translate.instant('finraSettings.dialog.savedMessage'))
          .afterClosed().subscribe(() => {
          this.isLabelCreated = true;
        });
      }, error: (error) => {
        this.enableLabelBtn = true;
        this.dialogService.complianceError(error.error.message);
      }
    });
  }

  enterProjectSettings(): void {
    this.projectLoadingSubject.next(true);
    const {gcpProject, bucketName} = this.projectForm.value;
    this.adminService.enterProjectSettings(gcpProject, bucketName).pipe(
      finalize(() => this.projectLoadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        this.projectSettings = resp;
        this.isProjectSettingsCreated = true;
        this.enableProjectSettingsBtn = false;
        this.dialogService.info(this.translate.instant('finraSettings.dialog.savedMessage'));
      }, error: (error) => {
        this.dialogService.complianceError(error.error.message);
      }
    });
  }

  getTechnicalLibraryId(field: keyof TechnicalLibrarySettings): string {
    return (this.technicalLibraries && this.technicalLibraries[field]) || '';
  }

  allTechnicalLibrariesExist(): boolean {
    return !!this.technicalLibraries && this.technicalLibraryFields.every(f => !!this.technicalLibraries[f]);
  }

  createTechnicalLibraries(): void {
    this.technicalLibrariesLoadingSubject.next(true);
    // The POST response is not the libraries payload, so reload it from the GET endpoint.
    this.adminService.createTechnicalLibraries().pipe(
      switchMap(() => this.adminService.getTechnicalLibraries()),
      finalize(() => this.technicalLibrariesLoadingSubject.next(false))
    ).subscribe({
      next: (resp) => {
        this.technicalLibraries = resp;
        this.dialogService.info(this.translate.instant('finraSettings.dialog.technicalLibrariesCreated'));
      }, error: (error) => {
        this.dialogService.complianceError(error.error.message);
        this.adminService.getTechnicalLibraries().pipe(take(1)).subscribe({
          next: (resp) => this.technicalLibraries = resp,
          error: (err) => console.error('Error fetching technical libraries:', err)
        });
      }
    });
  }

  isSuperAdmin(): boolean {
    return this.userService.isSuperAdmin();
  }

  getEnableProjectSettingsBtn(): boolean {
    return this.projectForm.valid && this.enableProjectSettingsBtn;
  }
}
